import { makeStyles } from '@material-ui/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import useListAction from 'core/hooks/useListAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import { listKaapiClusters } from '../../components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../../components/kaapi/clusters/selectors'
import CapacityCard from './CapacityCard'
import {
  DEFAULT_NODE_GROUP_STATUS,
  generateNodeGroupsStatus,
  generateWorkerNodesStatus,
  getBadgeVariant,
  getClusterStatus,
  isControlPlaneStatusReady,
  type NodeGroupStatus,
} from './helpers'
import StatusIconText, { type BadgeVariant } from './StatusIconText'

const StatusCardsHeader = () => {
  const classes = useStyles({})
  const { history } = useReactRouter()

  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  const [controlPlaneStatus, setControlPlaneStatus] = useState<NodeGroupStatus>(
    DEFAULT_NODE_GROUP_STATUS.success,
  )
  // Load Machine Deployments
  useListAction(listKaapiClusters)
  const clusters: any[] = useSelector(kaapiClustersOverviewSelector)
  const clusterData = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )
  const machineDeployments = clusterData?.machineDeployments || []
  const machines = clusterData?.machines || []

  const controlPlaneNodes = useMemo(() => {
    return machines.filter((m) => m.nodeType === 'Control Plane')
  }, [machines])

  const workerNodes = useMemo(() => {
    return machines.filter((m) => m.nodeType !== 'Control Plane')
  }, [machines])

  const isColocatedControlPlane = clusterData?.controlPlaneType === 'Colocated'

  // Map the machine deployments status to get the node groups status
  const nodeGroupsStatus = useMemo(() => {
    return generateNodeGroupsStatus(machineDeployments)
  }, [machineDeployments])

  const controlPlaneNodesStatus = useMemo(() => {
    return generateWorkerNodesStatus(controlPlaneNodes)
  }, [controlPlaneNodes])

  const workerNodesStatus = useMemo(() => {
    return generateWorkerNodesStatus(workerNodes)
  }, [workerNodes])

  // Control Plane Status
  const isControlPlaneReady = isControlPlaneStatusReady(clusterData)
  const isOnNodeGroupsTab = history.location.pathname.includes(`/${IClusterDetailTabs.NODE_GROUPS}`)
  const isOnNodesTab = history.location.pathname.includes(`/${IClusterDetailTabs.NODES}`)

  // Get Cluster Status
  const clusterStatus = useMemo(() => getClusterStatus(clusterData), [clusterData])

  useEffect(() => {
    if (isControlPlaneReady) {
      setControlPlaneStatus(DEFAULT_NODE_GROUP_STATUS.success)
    } else {
      setControlPlaneStatus(DEFAULT_NODE_GROUP_STATUS.error)
    }
  }, [isControlPlaneReady])

  // Only show status cards on NodeGroups and Nodes tabs
  if (!isOnNodeGroupsTab && !isOnNodesTab) {
    return null
  }

  const getGridClass = () => {
    if (isOnNodesTab) {
      // 4 cards for colocated (Overall Health, Control Plane, Control Plane Nodes, Worker Nodes)
      // 3 cards for managed (Overall Health, Control Plane, Worker Nodes)
      return isColocatedControlPlane ? classes.cardsRow : classes.cardsRowThreeColumns
    }
    // NodeGroups tab: 3 cards (Overall Health, Control Plane, Node Groups)
    return classes.cardsRowThreeColumns
  }

  return (
    <div className={getGridClass()} data-testid="status-cards-header">
      {/* Overall Health - shown on both NodeGroups and Nodes tabs */}
      <CapacityCard title="Overall Health" testId="overall-health-card">
        <StatusIconText
          icon={DEFAULT_NODE_GROUP_STATUS?.[getBadgeVariant(clusterStatus)]?.icon}
          variant={getBadgeVariant(clusterStatus)}
          label={clusterStatus}
          margin={false}
          data-testid="overall-health-status"
        />
      </CapacityCard>

      {/* Control Plane - shown on both NodeGroups and Nodes tabs */}
      <CapacityCard title="Control Plane" testId="control-plane-card">
        <StatusIconText
          label={
            isControlPlaneReady
              ? 'Ready'
              : clusterData?.hostedControlPlane?.status?.phase || 'Not Ready'
          }
          icon={controlPlaneStatus?.icon}
          variant={controlPlaneStatus?.variant as BadgeVariant}
          margin={false}
          data-testid="control-plane-status"
        />
      </CapacityCard>

      {/* Node Groups card - only on NodeGroups tab */}
      {isOnNodeGroupsTab && (
        <CapacityCard
          title="Node Groups"
          count={machineDeployments.length}
          testId="node-groups-card"
        >
          {nodeGroupsStatus.length ? (
            nodeGroupsStatus?.map((item, index) => (
              <StatusIconText
                key={index}
                label={item?.label || ''}
                icon={item?.icon}
                variant={item?.variant}
                rightBorder={index !== nodeGroupsStatus?.length - 1}
                margin={false}
                data-testid={`node-group-status-${index}`}
              />
            ))
          ) : (
            <div className={classes.emptyMessage}>No Node Groups</div>
          )}
        </CapacityCard>
      )}

      {/* Nodes cards - only on Nodes tab */}
      {isOnNodesTab && (
        <>
          {isColocatedControlPlane && (
            <CapacityCard
              title="Control Plane Nodes"
              count={controlPlaneNodes.length}
              testId="control-plane-nodes-card"
            >
              {controlPlaneNodesStatus.length > 0 ? (
                controlPlaneNodesStatus?.map((item, index) => (
                  <StatusIconText
                    key={`cp-${index}`}
                    label={item?.label || ''}
                    icon={item?.icon}
                    variant={item?.variant}
                    rightBorder={index !== controlPlaneNodesStatus?.length - 1}
                    margin={false}
                    data-testid={`control-plane-node-status-${index}`}
                  />
                ))
              ) : (
                <div className={classes.emptyMessage}>No Control Plane Nodes</div>
              )}
            </CapacityCard>
          )}
          <CapacityCard title="Worker Nodes" count={workerNodes.length} testId="worker-nodes-card">
            {workerNodesStatus.length > 0 ? (
              workerNodesStatus?.map((item, index) => (
                <StatusIconText
                  key={`w-${index}`}
                  label={item?.label || ''}
                  icon={item?.icon}
                  variant={item?.variant}
                  rightBorder={index !== workerNodesStatus?.length - 1}
                  margin={false}
                  data-testid={`worker-node-status-${index}`}
                />
              ))
            ) : (
              <div className={classes.emptyMessage}>No Worker Nodes</div>
            )}
          </CapacityCard>
        </>
      )}
    </div>
  )
}

export default StatusCardsHeader

const useStyles = makeStyles((theme: Theme) => ({
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    '& > div:nth-child(4n+1)': {
      borderRadius: '4px 0px 0px 4px',
    },
    '& > div:nth-child(4n)': {
      borderRadius: '0px 4px 4px 0px',
    },
  },
  cardsRowThreeColumns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    '& > div:nth-child(3n+1)': {
      borderRadius: '4px 0px 0px 4px',
    },
    '& > div:nth-child(3n)': {
      borderRadius: '0px 4px 4px 0px',
    },
  },
  nodesContentContainer: {
    display: 'flex',
    gap: '16px',
  },
  divider: {
    background: theme.components.card.border,
    border: 0,
    width: '1px',
  },
  emptyMessage: {
    paddingTop: '8px',
    paddingBottom: '12px',
  },
}))
